import type {
  ActedForView,
  ActForRole,
  PoolLatestChange,
  QueueRow,
  QuestionNow,
} from '@myrewards/contracts';
import { HELD_FOR_WORDS, QUESTION_KIND_WORDS, type QuestionKindId } from '@myrewards/domain';
import { capitalize, countWord } from '../home/labels';
import { LEDGER_KIND_WORD } from '../settings/model';
import {
  daysBetween,
  formatMoney,
  formatRecentTime,
  formatShortDate,
  organizationDate,
  organizationHour,
  plural,
} from '../ui';
import { firstName } from './words';
import { spotWorkHref } from '../manage/pages';

/** Everything done for the reader, a page at a time. */
export const DONE_FOR_YOU_HREF = '/done-for-you';

// APP-148: the words the Executive or Sector Lead a Stock Admin acted for reads: Done for you (the
// ExecHome and LeadHome boards), its hero numbers, a pool's latest change and "Not what you
// asked?" (the Question board). The question's kinds are the domain's, shared with the server.

/** "Not what you asked?": each kind, with the line under it (the Question board). */
export const QUESTION_KINDS: readonly { id: QuestionKindId; label: string; hint: string }[] = [
  {
    id: 'not_asked',
    label: QUESTION_KIND_WORDS.not_asked,
    hint: 'Nobody should have acted for me',
  },
  {
    id: 'different',
    label: QUESTION_KIND_WORDS.different,
    hint: 'The amount, the people or the decision',
  },
  { id: 'other', label: QUESTION_KIND_WORDS.other, hint: 'Tell us below' },
];

/** "no files", "1 file", "1 file: Year-end win names.xlsx" (named when no note says more), "2 files". */
function filesWords(view: ActedForView): string {
  const count = view.files.length;
  if (count === 0) return 'no files';
  const [only] = view.files;
  return count === 1 && only !== undefined && !hasNote(view)
    ? `1 file: ${only.fileName}`
    : plural(count, 'file', 'files');
}

const hasNote = (view: ActedForView): boolean => view.note !== null && view.note.trim() !== '';

/**
 * A Done for you row's line (the ExecHome and LeadHome boards): when, what it was, the note in the
 * Stock Admin's name, and the files. "Today, 9:34 AM PDT · HydroVision sea trials · 7 people ·
 * $21,000 · Meredith’s note: “Same Teams chat as SDB-2026-0156.” · no files".
 */
export function doneLine(view: ActedForView, now?: Date): string {
  return [
    formatRecentTime(view.at, now),
    view.facts,
    hasNote(view) ? `${firstName(view.actor.name)}’s note: “${view.note?.trim() ?? ''}”` : null,
    filesWords(view),
  ]
    .filter((part): part is string => part !== null && part !== '')
    .join(' · ');
}

/**
 * Where Open goes: the award's record (the Executive or lead may read it, G1), the budget pools
 * for a budget change, the Executive's values for a setting, Spot funding for a Spot decision.
 */
export function doneHref(view: ActedForView): string {
  if (view.award !== null) return `/awards/${view.award.id}`;
  switch (view.action) {
    case 'budget_change':
      return '/manage/executive/budget-pools';
    case 'setting_change':
      return '/manage/executive/values';
    default:
      return spotWorkHref('funding');
  }
}

/**
 * Where a row stands once the person has said it isn't what they asked: held, told without a
 * hold, or settled. Null while they haven't.
 */
export function questionedWords(
  view: ActedForView,
): { label: string; tone: 'problem' | 'waiting' | 'done' } | null {
  if (view.question === null) return null;
  const settled = view.settled;
  if (settled !== null) {
    const by = settled.by.name;
    switch (settled.outcome) {
      case 'confirmed':
        return { label: `Confirmed as it is by ${by}`, tone: 'done' };
      case 'returned':
        return { label: `Returned to be changed by ${by}`, tone: 'done' };
      case 'resolved':
        return { label: `Settled by ${by}`, tone: 'done' };
    }
  }
  return view.held
    ? { label: 'Held: you said it isn’t what you asked', tone: 'problem' }
    : { label: 'You said it isn’t what you asked', tone: 'waiting' };
}

/** What was done for the reader today: how many, by whom, and when in the day. */
export interface DoneToday {
  count: number;
  /** Each Stock Admin who did something today, newest first. */
  names: string[];
  /** "this morning", "this afternoon", "this evening", or "today" when it spans more. */
  when: string;
}

function partOfDay(at: string): string {
  const hour = organizationHour(at);
  if (hour < 12) return 'this morning';
  return hour < 17 ? 'this afternoon' : 'this evening';
}

/** Today's rows of Done for you (`asOf`: Home's day), or null when nobody acted for them today. */
export function doneToday(items: readonly ActedForView[], asOf: string): DoneToday | null {
  const today = items.filter((view) => daysBetween(organizationDate(view.at), asOf) === 0);
  if (today.length === 0) return null;
  const parts = new Set(today.map((view) => partOfDay(view.at)));
  const [only] = [...parts];
  return {
    count: today.length,
    names: [...new Set(today.map((view) => view.actor.name))],
    when: parts.size === 1 && only !== undefined ? only : 'today',
  };
}

/** "Meredith Okonkwo", "Meredith Okonkwo and Tyler Brandt", "Meredith Okonkwo and 2 others". */
function whoWords(names: readonly string[]): string {
  const [first, second] = names;
  if (names.length <= 1) return first ?? '';
  if (names.length === 2) return `${first ?? ''} and ${second ?? ''}`;
  return `${first ?? ''} and ${plural(names.length - 1, 'other', 'others')}`;
}

/** The hero number's line: "By Meredith Okonkwo · this morning". */
export function doneTodayMeta(done: DoneToday): string {
  return `By ${whoWords(done.names)} · ${done.when}`;
}

/** "Meredith Okonkwo did two things for you this morning; they’re under Done for you." */
export function doneTodaySentence(done: DoneToday): string {
  const things = done.count === 1 ? 'one thing' : `${countWord(done.count)} things`;
  const where = done.count === 1 ? 'it’s under Done for you' : 'they’re under Done for you';
  return `${whoWords(done.names)} did ${things} for you ${done.when}; ${where}.`;
}

/** "today", "yesterday", "Jan 1": when a pool last changed, counted to Home's day. */
function changedOn(at: string, asOf: string): string {
  const days = daysBetween(organizationDate(at), asOf);
  if (days === 0) return 'today';
  return days === 1 ? 'yesterday' : formatShortDate(at);
}

/**
 * A pool's latest change (the ExecHome and LeadHome boards' "Latest change"): "Addition +$25,000 ·
 * today · Meredith Okonkwo for Marcus Thornbury", "Top-up +$2,913 · today · Meredith Okonkwo for
 * you", "Initial budget · Jan 1". `meId`: the reader, who reads "for you".
 */
export function latestChangeWords(latest: PoolLatestChange, meId: string, asOf: string): string {
  const amount =
    latest.kind === 'initial'
      ? null
      : `${latest.cashCents < 0 ? '−' : '+'}${formatMoney(Math.abs(latest.cashCents))}`;
  const by =
    latest.by === null
      ? null
      : latest.for === null
        ? latest.by.name
        : `${latest.by.name} for ${latest.for.id === meId ? 'you' : latest.for.name}`;
  return [
    amount === null
      ? capitalize(LEDGER_KIND_WORD[latest.kind])
      : `${capitalize(LEDGER_KIND_WORD[latest.kind])} ${amount}`,
    changedOn(latest.at, asOf),
    by,
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');
}

/** "approved", "entered and sent": the action, after the award in the dialog's eyebrow. */
const DONE_VERB: Readonly<Record<ActedForView['action'], string>> = {
  approve: 'approved',
  decline: 'declined',
  reject: 'rejected',
  return: 'returned',
  record_decision: 'recorded',
  change_amounts: 'changed',
  change_split: 'changed',
  move_person: 'changed',
  enter_and_send: 'entered and sent',
  send: 'sent',
  resend: 'fixed and resent',
  withdraw: 'withdrawn',
  budget_change: 'changed',
  setting_change: 'changed',
  spot_funding_approve: 'decided',
  spot_funding_decline: 'decided',
};

/**
 * The dialog's eyebrow (the Question board): "SDB-2026-0156 · approved for you by Meredith
 * Okonkwo · today, 9:31 AM". Without an award, what was done says it: "Meredith Okonkwo added
 * $25,000 to … for you · today, 9:31 AM".
 */
export function questionEyebrow(view: ActedForView, now?: Date): string {
  const when = formatRecentTime(view.at, now).replace(/^(Today|Yesterday)\b/, (day) =>
    day.toLowerCase(),
  );
  if (view.award?.displayId == null) return `${view.line} · ${when}`;
  return `${view.award.displayId} · ${DONE_VERB[view.action]} for you by ${view.actor.name} · ${when}`;
}

/**
 * Where its owner sees an award, by its stage, as the Question board says it (R89): "With the
 * Stock Admin team", the stage's own name; an approval step "waiting for approval", never the
 * stage's label, which is "Approval" at every approval step.
 */
const STAGE_PHRASE: Readonly<Record<NonNullable<QuestionNow['stage']>, string>> = {
  draft: 'in draft',
  approval: 'waiting for approval',
  with_stock_admin: 'With the Stock Admin team',
  employee_choice: 'waiting on the employee’s choice',
  paid: 'paid',
  closed: 'closed',
};

/** The award is the reader's own (R24: a Sector Lead's, entered for them; an Executive's). */
const ownsIt = (view: ActedForView, asking: QuestionNow): boolean =>
  asking.owner !== null && asking.owner.id === view.for.id;

/** "return it to Hannah to change", or to the reader's own drafts when it's theirs. */
function returnTo(view: ActedForView, asking: QuestionNow): string {
  if (ownsIt(view, asking)) return 'your drafts';
  return asking.owner === null ? 'whoever entered it' : firstName(asking.owner.name);
}

/** "the $2,913 back out of Terrestrial", "the $2,913 and 500 options back out of PAA/TAA". */
function takesBack(asking: QuestionNow): string | null {
  if (asking.topUps.length === 0) return null;
  const each = asking.topUps.map((t) => {
    const amount =
      t.cashCents > 0 && t.options > 0
        ? `${formatMoney(t.cashCents)} and ${plural(t.options, 'option', 'options')}`
        : t.options > 0
          ? plural(t.options, 'option', 'options')
          : formatMoney(t.cashCents);
    return `the ${amount} back out of ${t.pool}`;
  });
  const last = each.pop() ?? '';
  return each.length === 0 ? last : `${each.join(', ')} and ${last}`;
}

/**
 * "What happens next" (the Question board), as it is for this row now: held before its next step
 * while it's going on; otherwise the team is told and nothing is held, with why.
 */
export function whatHappensNext(
  view: ActedForView,
  asking: QuestionNow,
): { icon: 'lock' | 'bell' | 'check' | 'info'; text: string }[] {
  const lead = view.for.role === 'sector_lead';
  const ref = view.award?.displayId ?? 'The award';
  const told = {
    icon: 'bell' as const,
    text: 'The System Admins and the Stock Admin team get a notice, and it tops their Waiting on you.',
  };
  if (asking.standing === 'holds') {
    const heldFor = HELD_FOR_WORDS[view.for.role];
    const who = ownsIt(view, asking) ? 'You' : asking.owner?.name;
    const seen =
      who === undefined || asking.stage === null
        ? ''
        : ` ${who} still ${who === 'You' ? 'see' : 'sees'} it ${STAGE_PHRASE[asking.stage]}, marked “${heldFor}”.`;
    const back = takesBack(asking);
    const settle = lead
      ? `Then you either confirm it as it is or return it to ${returnTo(view, asking)} to change.`
      : `Then you, or any other Executive, either confirm it as it is or return it to ${returnTo(view, asking)} to change.`;
    return [
      {
        icon: 'lock',
        text: `${ref} is held: nobody can check it or send letters until it’s settled.${seen}`,
      },
      told,
      {
        icon: 'check',
        text:
          back === null
            ? settle
            : `${settle} Returning it takes ${back}, recorded as a Reduction in the ledger.`,
      },
    ];
  }
  const why =
    asking.standing === 'no_award'
      ? view.action === 'spot_funding_approve' || view.action === 'spot_funding_decline'
        ? 'Nothing is held: Spot funding has no award to hold.'
        : 'Nothing is held: a change to a budget or a setting has no award to hold. You can change it back yourself.'
      : asking.standing === 'past_letters'
        ? `Nothing is held: ${ref === 'The award' ? 'its' : `${ref}’s`} letters have gone out since.`
        : asking.standing === 'moved_on'
          ? `Nothing is held: ${ref === 'The award' ? 'the award' : ref} has moved on since${asking.stage === null ? '' : `, and it’s ${STAGE_PHRASE[asking.stage]} now`}.`
          : `Nothing is held: ${ref === 'The award' ? 'the award' : ref} has stopped.`;
  // R92: where it's marked settled, only where a screen offers it: Done for you, and for a
  // question about an award, the award's record. N3: Done for you lists only what was done for
  // the reader, so other Executives reach it on the record, never under their Done for you.
  const record =
    view.award === null
      ? null
      : `${view.award.displayId === null ? 'the award' : `${view.award.displayId}’s`} record`;
  return [
    { icon: 'info', text: why },
    told,
    {
      icon: 'check',
      text:
        record === null
          ? 'Then you mark it settled under Done for you.'
          : lead
            ? `Then you mark it settled under Done for you or on ${record}.`
            : `Then you mark it settled under Done for you, or any Executive does on ${record}.`,
    },
  ];
}

/** The dialog's intro (the Question board), held or not. */
export function questionIntro(asking: QuestionNow, role: ActedForView['for']['role']): string {
  const decides = role === 'sector_lead' ? 'you decide' : 'an Executive decides';
  return asking.standing === 'holds'
    ? `Tell us what’s wrong. The award is held where it is, before its next step, and the System Admins and the Stock Admin team are told straight away. Nothing is undone until ${decides}.`
    : 'Tell us what’s wrong. The System Admins and the Stock Admin team are told straight away.';
}

/** Under "Tell us more": who reads it (the Question board). */
export function questionReaders(view: ActedForView): string {
  return `${firstName(view.actor.name)}, the Stock Admin team and the System Admins see this.`;
}

/** The dialog's action: "Hold it and tell them" while it would hold, else "Tell them". */
export function questionAction(asking: QuestionNow): string {
  return asking.standing === 'holds' ? 'Hold it and tell them' : 'Tell them';
}

// --- The hold, the record and the queue (Task 11b) -----------------------------------------------

/** Whose review a held award waits on (D17): one source with the server's (the domain's). */
export { HELD_FOR_WORDS };

/** Who settles a hold, by whom the questioned action was done for (D17). */
const SETTLER: Readonly<Record<ActForRole, string>> = {
  executive: 'an Executive settles it',
  sector_lead: 'the Sector Lead settles it',
};

/**
 * Under a held award's mark, for anyone who reads the record (the Question board's "still sees
 * it … marked"): what holds it, never the person's words, which only some readers may see (R33).
 */
export function heldStory(role: ActForRole): string {
  return `The person it was done for said it isn’t what they asked. Nobody can claim it, check it or send its letters until ${SETTLER[role]}.`;
}

/**
 * Who said it isn't what they asked, for whoever settles it: "You said", or "Elena Brightwater
 * said", then what's wrong (the Question board's kinds) and their own words.
 */
export function heldQuestion(view: ActedForView, meId: string): string | null {
  const q = view.question;
  if (q === null) return null;
  const who = q.by.id === meId ? 'You said' : `${q.by.name} said`;
  return `${who} it isn’t what was asked: ${QUESTION_KIND_WORDS[q.kind]}. “${q.text.trim()}”`;
}

/** What settling does, for whoever settles it: "Then you either confirm it as it is or …". */
export function settleChoice(role: ActForRole, owner: string): string {
  return role === 'sector_lead'
    ? `You either confirm it as it is or return it to ${owner} to change.`
    : `You, or any other Executive, either confirm it as it is or return it to ${owner} to change.`;
}

/**
 * The Stock Admin queue's mark, past tense as the Check board says it: "approved", "entered" (not
 * "entered and sent"), "withdrew". The rest are the Done for you verbs.
 */
const QUEUE_VERB: Readonly<Record<ActedForView['action'], string>> = {
  ...DONE_VERB,
  enter_and_send: 'entered',
  withdraw: 'withdrew',
};

type DoneMark = NonNullable<QueueRow['doneForOthers']>[number];

/** "You approved it for Elena Brightwater", "Tyler Brandt entered it for Graham Whitlock". */
export function queueMark(mark: DoneMark, meId: string): string {
  const who = mark.actor.id === meId ? 'You' : mark.actor.name;
  return `${who} ${QUEUE_VERB[mark.action]} it for ${mark.for.name}`;
}

/** "1 entered and 1 approved", "2 approved", "1 entered, 1 approved and 1 returned". */
function counted(verbs: ReadonlyMap<string, number>): string {
  const each = [...verbs].map(([verb, n]) => `${String(n)} ${verb}`);
  const last = each.pop() ?? '';
  return each.length === 0 ? last : `${each.join(', ')} and ${last}`;
}

/**
 * The note above the queue (the Check board): how many of the listed awards the reader acted on
 * for someone, what for whom, and that the team claims them all the same (D11: no identity rules).
 * "You did 4 of these for someone else: 2 approved for Elena Brightwater, 1 entered and 1 approved
 * for Graham Whitlock. Each row says so. Anyone on the team can claim them, you included." Null
 * when the reader did none. `count`: the server's count of rows, when it sends one.
 */
export function doneForOthersNote(
  rows: readonly Pick<QueueRow, 'doneForOthers'>[],
  meId: string,
  count?: number,
): string | null {
  const byPerson = new Map<string, { name: string; verbs: Map<string, number> }>();
  let mine = 0;
  for (const row of rows) {
    const marks = (row.doneForOthers ?? []).filter((m) => m.actor.id === meId);
    if (marks.length > 0) mine += 1;
    for (const mark of marks) {
      const person = byPerson.get(mark.for.id) ?? {
        name: mark.for.name,
        verbs: new Map<string, number>(),
      };
      const verb = QUEUE_VERB[mark.action];
      person.verbs.set(verb, (person.verbs.get(verb) ?? 0) + 1);
      byPerson.set(mark.for.id, person);
    }
  }
  const n = count ?? mine;
  if (n === 0 || byPerson.size === 0) return null;
  const what = [...byPerson.values()].map((p) => `${counted(p.verbs)} for ${p.name}`).join(', ');
  return n === 1
    ? `You did 1 of these for someone else: ${what}. Its row says so. Anyone on the team can claim it, you included.`
    : `You did ${String(n)} of these for someone else: ${what}. Each row says so. Anyone on the team can claim them, you included.`;
}

/**
 * Under the queue, beside the note (the Check board): what was done for someone still counts in
 * Waiting on you, for the reader as for the team. "Your Waiting on you counts all 6 waiting to be
 * claimed, as Tyler Brandt’s does."
 */
export function stillCountsNote(waiting: number, teammates: readonly { name: string }[]): string {
  const all = waiting === 1 ? 'the 1' : `all ${String(waiting)}`;
  const [only] = teammates;
  const others =
    teammates.length === 0
      ? ''
      : teammates.length === 1 && only !== undefined
        ? `, as ${only.name}’s does`
        : ', as the rest of the team’s do';
  return `Your Waiting on you counts ${all} waiting to be claimed${others}.`;
}
